import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MessageBoxComponent, MessageBoxParameters, MessageBoxType } from 'app/components/messageBox/messageBox.component';
import { ProcessDialogComponent } from 'app/main/content/processes/process-dialog/process-dialog.component';
import { Observable, of, ReplaySubject, Subscription, timer } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { Process, ProcessData } from '../../models/process';
import { AuthService } from '../auth.service';
import { DefaultDataService } from '../default-data.service';
import { EventService } from '../event.service';
import { ProcessTypeService } from '../process-type.service';
import { ProcessService } from '../process.service';
import { SubjectService } from '../subject.service';
import { UserService } from '../user.service';
import { LookupTypeService } from '../lookup-type.service';
import { LookupService } from '../lookup.service';
import { environment } from 'environments/environment';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';
import { messageService } from '../messages/message.module';

@AutoUnsubscribe()
@Injectable({ providedIn: 'root' })
export class SubjectStopwatchService implements OnDestroy {

  onShow: ReplaySubject<boolean> = new ReplaySubject<boolean>(1, 100);
  onTick: ReplaySubject<number> = new ReplaySubject<number>(1, 100);
  onStopped: ReplaySubject<any> = new ReplaySubject<any>(1, 100);
  onPaused: ReplaySubject<any> = new ReplaySubject<any>(1, 100);
  onResume: ReplaySubject<any> = new ReplaySubject<any>(1, 100);
  onShowTimeRecord: ReplaySubject<Process> = new ReplaySubject<Process>(1, 100);

  subjectUid: string;
  subjectTypeName: string;

  stopped = false;
  paused = false;
  running = false;
  seconds = 0;

  timer$: Subscription;

  startTime: Date = new Date();

  constructor(private subjectService: SubjectService,
    private processTypeService: ProcessTypeService,
    private defaultDataService: DefaultDataService,
    private userService: UserService,
    private processService: ProcessService,
    private eventService: EventService,
    private authService: AuthService,
    private lookupTypeService: LookupTypeService,
    private lookupService: LookupService,
    private auth: AuthService,
    private dialog: MatDialog) {
  }

  ngOnDestroy(): void {
    
  }

  checkTime(url: string): Observable<boolean> {
    if (this.running && this.defaultDataService.promptForTime) {
      return this.navigatedAway(url).pipe(take(1), switchMap((result) => {
        if (result) {
          // we are navigating away from the current subject, stop the timer...
          this.stop(false);
          // ...ask the user if they want to save time for this case
          return this.prompt().pipe(take(1), map((res) => {
            // we have navigated away, reset timer
            this.reset();
            return res;
          }));
        }
        else {
          return of(true);
        }
      }));
    }
    else {
      return of(true);
    }
  }

  showTimeRecord(): Observable<boolean> {
    return this.subjectService.getFullSubject(this.subjectUid).pipe(take(1), switchMap((subject) => {
      if (subject) {
        return this.processTypeService.getByName('Time Record').pipe(take(1), switchMap((processType) => {
          if (processType) {
            const process = new Process();
            process.processTypeId = processType.id;
            process.subjectId = subject.id;
            process.processData = new ProcessData();
            process.processData.processTypeId = processType.id;

            // we now need to auto populate
            process.addData(processType, 'summary', 'Time working on ' + subject.summary);

            var timeValue = (this.seconds) ? Math.round(this.seconds / 36) / 100 : 0; // number of elapsed hours to 2 dpl
            if (environment.timeRecordingInMinutes === true) {
              timeValue = (this.seconds) ? Math.round(this.seconds / 60) : 0; // number of elapsed minutes
            }
            process.addData(processType, 'timevalue', timeValue);
            process.addData(processType, 'hoursvalue', timeValue);
            process.addData(processType, 'dateadded', new Date());

            // if subject is defined, the use the user from that subject in place of the current user. (For Hertfordshire)            
            var userId = this.userService.getCurrentUserId();
            if (subject.subjectUsers.length > 0 && subject.subjectUsers[0].userId) userId = subject.subjectUsers[0].userId;
            process.addData(processType, 'user', userId);

            // Add default organisation to time record (For Hertfordshire)
            process.addDefaultOrganisation(subject, processType, this.lookupService, this.lookupTypeService);

            // Get cost for selected user.
            const user = this.userService.getUserById(userId);
            if (user.costPerHour) {
              process.addData(processType, 'costvalue', Math.round(((user.costPerHour || 0) * ((this.seconds) ? (this.seconds / 3600) : 0)) * 100) / 100);
            }

            // set hidden flag to force the dialog to check validity/enable save button as appropriate when opened
            (process as any).enableSave = true;

            return this.processService.hydrate(process).pipe(take(1), switchMap((p) => {
              return this.dialog.open(ProcessDialogComponent, {
                data: process,
              }).afterClosed();
            }));
          }
          else {
            return of(false);
          }
        }));
      }
      return of(false);
    }));
  }

  start(subjectUid) {
    // only start/display stopwatch if prompt for time is set to true
    if (this.defaultDataService.promptForTime && (!this.authService.isUserReadOnly())) {
      if (subjectUid) {
        this.subjectService.get(subjectUid).pipe(take(1)).subscribe((subject) => {
          this.subjectTypeName = this.subjectTypeName || 'subject';
          if (subject && subject.subjectType && subject.subjectType.primary && (!(this.subjectUid && (subject.uid === this.subjectUid)))) {
            this.reset();
            this.subjectUid = subjectUid;
            this.subjectTypeName = subject.subjectType.name;
            this.running = true;
            this.onShow.next(true);

            this.startTime = new Date();

            this.timer$ = timer(1000, 1000).pipe(filter(() => !(this.paused || this.stopped))).subscribe(() => {

              // Get time in seconds between calls.              
              var d = new Date();
              const secondsBetween = Math.abs(d.getTime() - this.startTime.getTime()) / 1000;
              this.seconds += secondsBetween;
              this.startTime = d;

              // this.seconds += 1;
              this.onTick.next(this.seconds);
            });
          }
        });
      }
    }
  }

  stop(showPrompt: boolean = true, logoutWhenDone:boolean=false) {
    this.stopped = true;
    this.onStopped.next(undefined);
    if (showPrompt) {
      this.prompt().subscribe(() => {
        this.seconds = 0;
        this.onTick.next(this.seconds);        

        messageService.success('Process Saved');

        if (logoutWhenDone)
          this.auth.logout();
      });
    }
  }

  pause() {
    this.paused = true;
    this.onPaused.next(undefined);
  }

  resume() {
    if (this.stopped === true) {
      this.seconds = 0;
    }

    // set startTime to current date and time 
    this.startTime = new Date();

    this.stopped = false;
    this.paused = false;
    this.onResume.next(undefined);
  }

  reset() {
    this.stopped = false;
    this.paused = false;
    this.seconds = 0;
    this.subjectUid = '';
    if (this.timer$) {
      this.timer$.unsubscribe();
    }
    this.onResume.next(undefined);
    this.running = false;
    this.onShow.next(false);
  }

  private prompt(): Observable<boolean> {
    // ...ask the user if they want to save time for this case
    const name = (this.subjectTypeName || 'subject').toLowerCase();
    const info = new MessageBoxParameters('Do you want to save time for this ' + name + '?', 'Save Time Taken', MessageBoxType.YesNo);
    return this.dialog.open(MessageBoxComponent, { data: info, disableClose: false, autoFocus: false }).afterClosed().pipe(take(1), switchMap((res) => {
      if (res === 'yes') {
        // user wants to save time for this case - display time record dialog         
        return this.showTimeRecord().pipe(take(1), map(() => {
          // notify any listeners that a time record has been updated
          this.eventService.updateTimeRecord();
          return true;
        }));
      }
      else {
        return of(true);
      }
    }));
  }

  private navigatedAway(url: string): Observable<boolean> {
    // if we have passed true then return true
    if (url === 'true') {
      return of(true);
    }

    // if
    // the url is for the last primary subject that we checked
    // the url is for a subject which is NOT a primary subject
    // the url is for note manager 
    if (url) {
      const params = url.split('/');
      if (params && (params.length > 3)) {
        const module = params[1].toLowerCase();

        // if we are navigating to notes then no prompt needed, continue
        if (module === 'notes') {
          return of(false);
        }

        // if we are navigating to a subject...
        let uid;
        switch(module){
          case 'referrals': uid = params[4]; break;
          case 'subjects': uid = params[3]; break;
          default: break;
        }
        if (uid) {

          // does uid contain hyphens?
          if (uid.includes('-')) {

            // does uid match the uid of the current subject we are timing?
            if (uid === this.subjectUid) {
              // matches current subject, no prompt needed, continue
              return of(false);
            }

            // is the subject type NOT a primary type?
            return this.subjectService.get(uid).pipe(take(1), map((subject) => {
              if (subject && subject.subjectType) {
                return subject.subjectType.primary ? true : false;
              }
              else {
                return true;
              }
            }));
          }
        }
      }
    }
    // if get here then we have navigated away, return true
    return of(true);
  }
}
